import { useState, useRef, useEffect, FormEvent, ChangeEvent } from 'react';

interface FormData {
  company: string;
  damdang: string;
  tel: string;
  email: string;
  moon_type: string;
  su_type: string;
  moon_cont: string;
}

const INITIAL_FORM: FormData = {
  company: '',
  damdang: '',
  tel: '',
  email: '',
  moon_type: '',
  su_type: '',
  moon_cont: '',
};

const API_ENDPOINT = 'https://www.smartcap.co.kr/api/submit_moonwi.php';

const CTASection = () => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0');
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.moon_cont.length > 500) return;

    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success === true) {
        setStatus('success');
        setFormData(INITIAL_FORM);
      } else {
        setStatus('error');
        setErrorMsg(result.message || '서버에서 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    } catch {
      setStatus('error');
      setErrorMsg('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    }
  };

  const inputClass =
    'w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1B2A4A] placeholder-gray-300 focus:outline-none focus:border-[#FF6B35] transition-colors bg-white';
  const labelClass = 'block text-xs font-semibold text-[#1B2A4A] mb-1.5';

  return (
    <section id="cta" className="py-24 bg-[#F8F9FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div className="text-center mb-16">
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Contact</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#1B2A4A] font-display">
            스마트캡 도입을 검토하고 계신가요?
          </h2>
          <p className="mt-4 text-gray-500 text-base max-w-xl mx-auto">
            문의 접수 후 영업일 기준 24시간 이내에 담당자가 연락드립니다.
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        <div
          ref={ref}
          className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 opacity-0 translate-y-8 transition-all duration-700"
        >
          {/* 좌측 정보 */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">빠른 견적 상담</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                샘플 요청, OEM 협의, 대리점 문의까지 모든 문의를 빠르게 처리해드립니다.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              {[
                { icon: 'ri-phone-fill', label: '전화 문의', value: '010-8640-3977' },
                { icon: 'ri-mail-fill', label: '이메일', value: 'myhomeis3977@hanmail.net' },
                { icon: 'ri-time-fill', label: '영업시간', value: '평일 09:00 ~ 18:00' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FF6B35]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#FF6B35] text-sm`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">{item.label}</p>
                    <p className="text-[#1B2A4A] font-medium text-sm">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 특허 카드 */}
            <div className="bg-[#1B2A4A] rounded-xl p-5 text-white">
              <i className="ri-award-fill text-[#FF6B35] text-2xl mb-2 block" />
              <p className="font-bold text-sm mb-1">특허 출원 완료 제품</p>
              <p className="text-white/60 text-xs leading-relaxed">
                출원번호 10-2024-0173136<br />
                대표 김경원 · 스마트캡
              </p>
            </div>

            {/* 문의 유형 안내 */}
            <div className="grid grid-cols-2 gap-2">
              {['견적 문의', '샘플 요청', '대리점 모집', 'OEM 협의'].map((tag) => (
                <div
                  key={tag}
                  className="text-center text-xs font-medium text-[#1B2A4A] bg-white border border-gray-200 rounded-lg py-2 px-3"
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>

          {/* 우측 폼 */}
          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 border border-gray-100"
            >
              {/* 회사명 + 담당자명 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>
                    회사명 <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="(주)회사명"
                    value={formData.company}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    담당자명 <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="text"
                    name="damdang"
                    required
                    placeholder="홍길동"
                    value={formData.damdang}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* 연락처 + 이메일 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>
                    연락처 <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="tel"
                    required
                    placeholder="010-0000-0000"
                    value={formData.tel}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>이메일</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="example@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* 문의유형 + 예상수량 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={labelClass}>
                    문의 유형 <span className="text-[#FF6B35]">*</span>
                  </label>
                  <select
                    name="moon_type"
                    required
                    value={formData.moon_type}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">선택해 주세요</option>
                    <option value="견적">견적 문의</option>
                    <option value="샘플">샘플 요청</option>
                    <option value="대리점">대리점 모집</option>
                    <option value="OEM">OEM 협의</option>
                    <option value="기타">기타</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>예상 수량</label>
                  <select
                    name="su_type"
                    value={formData.su_type}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">선택해 주세요</option>
                    <option value="1만 이하">1만 개 이하</option>
                    <option value="1~10만">1만 ~ 10만 개</option>
                    <option value="10~50만">10만 ~ 50만 개</option>
                    <option value="50만 이상">50만 개 이상</option>
                  </select>
                </div>
              </div>

              {/* 상세 내용 */}
              <div className="mb-6">
                <label className={labelClass}>
                  상세 내용 <span className="text-[#FF6B35]">*</span>
                  <span
                    className={`float-right font-normal ${
                      formData.moon_cont.length > 450 ? 'text-red-400' : 'text-gray-400'
                    }`}
                  >
                    {formData.moon_cont.length}/500
                  </span>
                </label>
                <textarea
                  name="moon_cont"
                  required
                  rows={4}
                  maxLength={500}
                  value={formData.moon_cont}
                  onChange={handleChange}
                  placeholder="시공 현장 규모, 수량, 납기 일정 등 궁금한 사항을 자유롭게 적어주세요."
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* 상태 메시지 */}
              {status === 'success' && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500" />
                  <p className="text-green-700 text-sm">
                    문의가 성공적으로 접수되었습니다. 영업일 기준 24시간 이내에 연락드리겠습니다.
                  </p>
                </div>
              )}
              {status === 'error' && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-center gap-2">
                  <i className="ri-error-warning-fill text-red-500" />
                  <p className="text-red-700 text-sm">{errorMsg || '전송 중 오류가 발생했습니다. 다시 시도해 주세요.'}</p>
                </div>
              )}

              {/* 제출 버튼 */}
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-[#FF6B35] text-white font-semibold py-3.5 rounded-md hover:bg-[#E5561F] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {status === 'sending' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    전송 중...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" />
                    문의하기
                  </>
                )}
              </button>

              <p className="text-center text-gray-400 text-xs mt-3">
                제출 시 개인정보 수집·이용에 동의하는 것으로 간주됩니다.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
